import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../https.service';
import { URLUtils } from '../urlUtils';

/**
 * Client history (LexiZ client detail screen).
 *
 * Every endpoint lives on the admin API, so all calls go through the
 * `sendRe*` side of HttpService. Those methods take the query string already
 * interpolated, so params are built and encoded here.
 */
@Injectable({
  providedIn: 'root'
})
export class ClientHistoryService {

  constructor(private httpService: HttpService) {}

  private toQuery(params: any): string {
    if (!params) {
      return '';
    }

    const parts: string[] = [];

    Object.keys(params).forEach(key => {
      const value = params[key];

      if (value !== null && value !== undefined && value !== '') {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });

    return parts.length ? `?${parts.join('&')}` : '';
  }

  getSummary(clientId: string): Observable<any> {
    return this.httpService.sendReGetRequest(
      URLUtils.clientHistorySummary(clientId)
    ) as Observable<any>;
  }

  getAppointments(clientId: string, params?: any): Observable<any> {
    return this.httpService.sendReGetRequest(
      URLUtils.clientHistoryAppointments(clientId) + this.toQuery(params)
    ) as Observable<any>;
  }

  getAppointmentDetail(clientId: string, appointmentId: string): Observable<any> {
    return this.httpService.sendReGetRequest(
      URLUtils.clientHistoryAppointmentDetail(clientId, appointmentId)
    ) as Observable<any>;
  }

  getPayments(clientId: string, params?: any): Observable<any> {
    return this.httpService.sendReGetRequest(
      URLUtils.clientHistoryPayments(clientId) + this.toQuery(params)
    ) as Observable<any>;
  }

  // Takes the order id — the only id every payment row carries.
  getPaymentDetail(clientId: string, paymentId: string): Observable<any> {
    return this.httpService.sendReGetRequest(
      URLUtils.clientHistoryPaymentDetail(clientId, paymentId)
    ) as Observable<any>;
  }

  getRefunds(clientId: string, params?: any): Observable<any> {
    return this.httpService.sendReGetRequest(
      URLUtils.clientHistoryRefunds(clientId) + this.toQuery(params)
    ) as Observable<any>;
  }

  // Takes our refund id — a refund that has not reached the gateway yet has
  // no razorpay refund id.
  getRefundDetail(clientId: string, refundId: string): Observable<any> {
    return this.httpService.sendReGetRequest(
      URLUtils.clientHistoryRefundDetail(clientId, refundId)
    ) as Observable<any>;
  }

  getActivity(clientId: string, params?: any): Observable<any> {
    return this.httpService.sendReGetRequest(
      URLUtils.clientHistoryActivity(clientId) + this.toQuery(params)
    ) as Observable<any>;
  }
}
